'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'

// Schema version - bump this when column structure changes
const SCHEMA_VERSION = 1

/**
 * Column configuration stored in localStorage
 */
interface ColumnConfig {
  /** Column identifier (matches column id or accessorKey) */
  id: string
  /** Whether column is visible */
  visible: boolean
}

/**
 * Stored preferences schema with version for migration
 */
interface StoredPreferences {
  version: number
  columns: ColumnConfig[]
}

/**
 * Column definition for the hook (subset of TanStack column def)
 */
export interface ColumnDefinition {
  /** Unique column identifier */
  id: string
  /** Display label for the column */
  label: string
  /** Whether column can be hidden (default: true) */
  canHide?: boolean
}

/**
 * Return type for useColumnPreferences hook
 */
export interface UseColumnPreferencesReturn {
  /** Current column order with visibility */
  columns: ColumnConfig[]
  /** Check if a column is visible */
  isVisible: (columnId: string) => boolean
  /** Toggle column visibility */
  toggleColumn: (columnId: string) => void
  /** Set column visibility explicitly */
  setColumnVisible: (columnId: string, visible: boolean) => void
  /** Move a column to a new position */
  moveColumn: (fromIndex: number, toIndex: number) => void
  /** Reset to default configuration */
  resetToDefaults: () => void
  /** Get ordered, visible column IDs (for TanStack Table columnOrder) */
  visibleColumnIds: string[]
  /** Get all column IDs in order (for TanStack Table columnOrder) */
  columnOrder: string[]
  /** Get column visibility map (for TanStack Table columnVisibility) */
  columnVisibility: Record<string, boolean>
  /** Whether preferences differ from defaults (hidden columns or reordered) */
  isModified: boolean
}

/**
 * Get localStorage key for a table type
 */
function getStorageKey(tableKey: string): string {
  return `award-table-columns-${tableKey}`
}

/**
 * Validate stored preferences against current column definitions.
 * Returns null if validation fails (should reset to defaults).
 */
function validatePreferences(
  stored: StoredPreferences | null,
  defaultColumns: ColumnDefinition[],
): ColumnConfig[] | null {
  // No stored preferences
  if (!stored) return null

  // Version mismatch - reset
  if (stored.version !== SCHEMA_VERSION) return null

  // No columns stored
  if (!stored.columns || !Array.isArray(stored.columns)) return null

  const defaultColumnIds = new Set(defaultColumns.map((c) => c.id))
  const storedColumnIds = new Set(stored.columns.map((c) => c.id))

  // Check for missing columns (removed from schema) - filter them out
  const validStoredColumns = stored.columns.filter((c) =>
    defaultColumnIds.has(c.id),
  )

  // Check for new columns (added to schema) - append them as visible
  const newColumns = defaultColumns
    .filter((c) => !storedColumnIds.has(c.id))
    .map((c) => ({ id: c.id, visible: true }))

  // If we have at least some valid columns, merge with new ones
  if (validStoredColumns.length > 0) {
    return [...validStoredColumns, ...newColumns]
  }

  // Everything is invalid - reset
  return null
}

/**
 * Load preferences from localStorage with validation
 */
function loadPreferences(
  tableKey: string,
  defaultColumns: ColumnDefinition[],
): ColumnConfig[] {
  if (typeof window === 'undefined') {
    return defaultColumns.map((c) => ({ id: c.id, visible: true }))
  }

  try {
    const key = getStorageKey(tableKey)
    const raw = localStorage.getItem(key)

    if (!raw) {
      return defaultColumns.map((c) => ({ id: c.id, visible: true }))
    }

    const stored = JSON.parse(raw) as StoredPreferences
    const validated = validatePreferences(stored, defaultColumns)

    if (validated) {
      return validated
    }

    // Validation failed - return defaults
    return defaultColumns.map((c) => ({ id: c.id, visible: true }))
  } catch {
    // Parse error - return defaults
    return defaultColumns.map((c) => ({ id: c.id, visible: true }))
  }
}

/**
 * Save preferences to localStorage
 */
function savePreferences(tableKey: string, columns: ColumnConfig[]): void {
  if (typeof window === 'undefined') return

  try {
    const key = getStorageKey(tableKey)
    const data: StoredPreferences = {
      version: SCHEMA_VERSION,
      columns,
    }
    localStorage.setItem(key, JSON.stringify(data))
  } catch {
    // Storage full or disabled - ignore
  }
}

/**
 * Hook for managing table column preferences with localStorage persistence.
 *
 * Features:
 * - Persists column visibility and order to localStorage
 * - Versioned schema for safe migrations
 * - Validates stored config against current columns
 * - Handles new/removed columns gracefully
 *
 * @param tableKey - Unique key for this table (e.g., 'transactions', 'orders')
 * @param defaultColumns - Array of column definitions with id and label
 *
 * @example
 * ```tsx
 * const columnDefs = [
 *   { id: 'date', label: 'Date' },
 *   { id: 'amount', label: 'Amount' },
 *   { id: 'description', label: 'Description' },
 * ]
 *
 * const { columns, isVisible, toggleColumn, moveColumn, resetToDefaults } =
 *   useColumnPreferences('transactions', columnDefs)
 *
 * // Filter TanStack columns based on visibility
 * const visibleColumns = useMemo(
 *   () => tanstackColumns.filter((col) => isVisible(col.id ?? col.accessorKey)),
 *   [tanstackColumns, isVisible]
 * )
 * ```
 */
export function useColumnPreferences(
  tableKey: string,
  defaultColumns: ColumnDefinition[],
): UseColumnPreferencesReturn {
  // Initialize state from localStorage
  const [columns, setColumns] = useState<ColumnConfig[]>(() =>
    loadPreferences(tableKey, defaultColumns),
  )

  // Re-validate when defaultColumns change (handles hot reload / dynamic columns)
  useEffect(() => {
    const validated = validatePreferences(
      { version: SCHEMA_VERSION, columns },
      defaultColumns,
    )
    if (!validated) {
      // Reset to defaults if validation fails
      const defaults = defaultColumns.map((c) => ({ id: c.id, visible: true }))
      setColumns(defaults)
      savePreferences(tableKey, defaults)
    } else {
      // Check if validated result differs from current (new/removed columns)
      const currentIds = columns.map((c) => c.id).join(',')
      const validatedIds = validated.map((c) => c.id).join(',')
      if (currentIds !== validatedIds) {
        setColumns(validated)
        savePreferences(tableKey, validated)
      }
    }
  }, [defaultColumns, tableKey]) // oxlint-disable-line react/exhaustive-deps

  // Save to localStorage when columns change
  useEffect(() => {
    savePreferences(tableKey, columns)
  }, [tableKey, columns])

  // Check if column is visible
  const isVisible = useCallback(
    (columnId: string): boolean => {
      const col = columns.find((c) => c.id === columnId)
      return col?.visible ?? true
    },
    [columns],
  )

  // Toggle column visibility
  const toggleColumn = useCallback((columnId: string): void => {
    setColumns((prev) =>
      prev.map((c) => (c.id === columnId ? { ...c, visible: !c.visible } : c)),
    )
  }, [])

  // Set column visibility explicitly
  const setColumnVisible = useCallback(
    (columnId: string, visible: boolean): void => {
      setColumns((prev) =>
        prev.map((c) => (c.id === columnId ? { ...c, visible } : c)),
      )
    },
    [],
  )

  // Move column to new position
  const moveColumn = useCallback((fromIndex: number, toIndex: number): void => {
    setColumns((prev) => {
      const newColumns = [...prev]
      const [moved] = newColumns.splice(fromIndex, 1)
      newColumns.splice(toIndex, 0, moved)
      return newColumns
    })
  }, [])

  // Reset to default configuration
  const resetToDefaults = useCallback((): void => {
    const defaults = defaultColumns.map((c) => ({ id: c.id, visible: true }))
    setColumns(defaults)
  }, [defaultColumns])

  // Get ordered, visible column IDs
  const visibleColumnIds = useMemo(
    () => columns.filter((c) => c.visible).map((c) => c.id),
    [columns],
  )

  // Get all column IDs in order (for TanStack Table columnOrder)
  const columnOrder = useMemo(() => columns.map((c) => c.id), [columns])

  // Get column visibility map (for TanStack Table columnVisibility)
  const columnVisibility = useMemo(
    () =>
      columns.reduce(
        (acc, c) => {
          acc[c.id] = c.visible
          return acc
        },
        {} as Record<string, boolean>,
      ),
    [columns],
  )

  // Check if preferences differ from defaults (hidden columns or reordered)
  const isModified = useMemo(() => {
    // Check if any column is hidden
    const hasHiddenColumns = columns.some((c) => !c.visible)
    if (hasHiddenColumns) return true

    // Check if order differs from default
    const defaultOrder = defaultColumns.map((c) => c.id)
    const currentOrder = columns.map((c) => c.id)

    // Different lengths means columns were added/removed (shouldn't happen normally)
    if (defaultOrder.length !== currentOrder.length) return true

    // Compare order
    return defaultOrder.some((id, index) => currentOrder[index] !== id)
  }, [columns, defaultColumns])

  return {
    columns,
    isVisible,
    toggleColumn,
    setColumnVisible,
    moveColumn,
    resetToDefaults,
    visibleColumnIds,
    columnOrder,
    columnVisibility,
    isModified,
  }
}
